import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Upload,
  User,
  Trash2,
  Crown,
  AlertTriangle,
  CreditCard,
  LogOut,
  Lock,
  Eye,
  EyeOff,
  Shield,
  XCircle,
  MapPin,
  ChevronRight,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/context/ThemeContext";
import LocationSettings from "@/components/LocationSettings";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useUserSubscription } from "@/utils/subscriptionUtils";
import TopBar from "@/components/layout/TopBar";
import { paymentService } from "@/services/paymentService";

const Profile = () => {
  const navigate = useNavigate();
  const { user, signOut } = useSupabaseAuth();
  const { theme } = useTheme();
  const { toast } = useToast();
  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [bio, setBio] = useState("");
  const [interests, setInterests] = useState<string[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { tier } = useUserSubscription();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  // True when the account was created via OAuth only (no email/password).
  const isOAuthUser = !user?.identities?.some(
    (id: { provider: string }) => id.provider === "email",
  );
  const [showCancelSubDialog, setShowCancelSubDialog] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] =
    useState(false);
  const [activeTab, setActiveTab] = useState<
    "profile" | "security" | "location" | "subscription" | "danger"
  >("profile");

  // Password change states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }

    // Fetch profile data
    const fetchProfile = async () => {
      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) throw error;

        if (profile) {
          setFullName((profile as any).full_name || "");
          setDateOfBirth((profile as any).date_of_birth || "");
          setBio((profile as any).bio || "");
          setInterests((profile as any).interests || []);

          // If there's an avatar_url, get a signed URL
          if ((profile as any).avatar_url) {
            const { data } = await supabase.storage
              .from("avatars")
              .createSignedUrl((profile as any).avatar_url, 3600);

            if (data) {
              setAvatarUrl(data.signedUrl);
            }
          } else {
            setAvatarUrl(null);
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        setAvatarUrl(null);
      }
    };

    fetchProfile();
  }, [user, navigate]);

  const handleAvatarUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      setIsUploading(true);
      const file = event.target.files?.[0];
      if (!file) return;

      console.log("Starting avatar upload...", file);

      // Validate file type and size
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(
          "Please upload a valid image file (JPG, PNG, GIF, or WebP)",
        );
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("File size should be less than 5MB");
      }

      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(2)}${fileExt ? `.${fileExt}` : ""}`;

      console.log("Uploading to Supabase storage...", fileName);

      // Upload to avatars bucket
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw uploadError;
      }

      // Store just the filename in the profile — use safe update/insert to avoid nulling credits_balance
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      let updateError: any = null;
      if (existingProfile) {
        const { error: err } = await supabase
          .from("profiles")
          .update({
            avatar_url: fileName,
            full_name: fullName,
            updated_at: new Date().toISOString(),
          } as any)
          .eq("id", user.id);
        updateError = err;
      } else {
        const { error: err } = await supabase.from("profiles").insert([
          {
            id: user.id,
            avatar_url: fileName,
            full_name: fullName,
            updated_at: new Date().toISOString(),
          },
        ]);
        updateError = err;
      }

      if (updateError) {
        console.error("Update error:", updateError);
        throw updateError;
      }

      // Get a signed URL for immediate display
      const { data, error: signedUrlError } = await supabase.storage
        .from("avatars")
        .createSignedUrl(fileName, 3600); // URL valid for 1 hour

      if (signedUrlError || !data) {
        console.error("Error getting signed URL:", signedUrlError);
        throw signedUrlError;
      }

      // Update local state with the signed URL
      setAvatarUrl(data.signedUrl);

      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Error uploading avatar:", error);
      toast({
        variant: "destructive",
        title: "Error uploading avatar",
        description:
          error.message || "There was an error updating your profile picture.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      setIsDeleting(true);

      if (!avatarUrl) return;

      // Get current avatar path from profile
      const { data: profile, error: fetchError } = await supabase
        .from("profiles")
        .select("avatar_url")
        .eq("id", user.id)
        .single();

      if (fetchError) throw fetchError;

      if (profile && (profile as any).avatar_url) {
        // Delete the file from storage
        const { error: deleteError } = await supabase.storage
          .from("avatars")
          .remove([(profile as any).avatar_url]);

        if (deleteError) throw deleteError;

        // Update profile to remove avatar_url
        const { error: updateError } = await supabase
          .from("profiles")
          .update({
            avatar_url: null,
            updated_at: new Date().toISOString(),
          } as any)
          .eq("id", user.id);

        if (updateError) throw updateError;

        // Update local state
        setAvatarUrl(null);

        toast({
          title: "Avatar deleted",
          description: "Your profile picture has been removed successfully.",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting avatar",
        description:
          error.message || "There was an error removing your profile picture.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setIsSaving(true);

      // Prefer update over upsert to avoid accidentally writing NULL for credits_balance.
      const { data: existing } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("profiles")
          .update({
            full_name: fullName,
            date_of_birth: dateOfBirth || null,
            bio: bio || null,
            interests: interests,
            updated_at: new Date().toISOString(),
          } as any)
          .eq("id", user.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("profiles").insert([
          {
            id: user.id,
            full_name: fullName,
            date_of_birth: dateOfBirth || null,
            bio: bio || null,
            interests: interests,
            updated_at: new Date().toISOString(),
          } as any,
        ]);

        if (error) throw error;
      }

      toast({
        title: "Profile updated",
        description: "Your profile information has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        variant: "destructive",
        title: "Error updating profile",
        description:
          error.message || "There was an error updating your profile.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast({
        variant: "destructive",
        title: isOAuthUser ? "Confirmation required" : "Password required",
        description: isOAuthUser
          ? "Please type DELETE to confirm account deletion."
          : "Please enter your password to confirm account deletion.",
      });
      return;
    }

    if (isOAuthUser && deletePassword !== "DELETE") {
      toast({
        variant: "destructive",
        title: "Confirmation required",
        description: "Please type DELETE (all caps) to confirm.",
      });
      return;
    }

    try {
      setIsDeletingAccount(true);

      // Get the current session token to pass to the Edge Function
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session.");

      // Call the Supabase Edge Function which re-authenticates, wipes all
      // data, then removes the auth.users record via the service role.
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ password: deletePassword }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to delete account.");
      }

      toast({
        title: "Account deleted",
        description:
          "Your account and all associated data have been permanently removed.",
      });

      // Sign out locally and redirect
      await signOut();
      navigate("/");
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast({
        variant: "destructive",
        title: "Error deleting account",
        description:
          error.message ||
          "There was an error deleting your account. Please contact support.",
      });
    } finally {
      setIsDeletingAccount(false);
      setDeletePassword("");
      setShowDeleteDialog(false);
    }
  };

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
      isValid:
        minLength &&
        hasUpperCase &&
        hasLowerCase &&
        hasNumbers &&
        hasSpecialChar,
    };
  };

  const handlePasswordChange = async () => {
    try {
      setIsChangingPassword(true);

      // Validation
      if (!currentPassword.trim()) {
        toast({
          variant: "destructive",
          title: "Current Password Required",
          description: "Please enter your current password.",
        });
        return;
      }

      if (!newPassword.trim()) {
        toast({
          variant: "destructive",
          title: "New Password Required",
          description: "Please enter a new password.",
        });
        return;
      }

      if (newPassword !== confirmPassword) {
        toast({
          variant: "destructive",
          title: "Passwords Don't Match",
          description: "New password and confirmation password must match.",
        });
        return;
      }

      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.isValid) {
        toast({
          variant: "destructive",
          title: "Weak Password",
          description: "Password must meet all security requirements.",
        });
        return;
      }

      if (currentPassword === newPassword) {
        toast({
          variant: "destructive",
          title: "Same Password",
          description:
            "New password must be different from your current password.",
        });
        return;
      }

      // Update password - Supabase will verify the current session automatically
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        // Handle specific error cases
        if (
          error.message?.includes("session_not_found") ||
          error.message?.includes("invalid_grant")
        ) {
          toast({
            variant: "destructive",
            title: "Session Expired",
            description: "Please sign out and sign back in, then try again.",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error Changing Password",
            description:
              error.message || "There was an error changing your password.",
          });
        }
        return;
      }

      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);

      toast({
        title: "Password Updated",
        description: "Your password has been changed successfully.",
      });
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast({
        variant: "destructive",
        title: "Error Changing Password",
        description:
          error.message || "There was an error changing your password.",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user) return;
    setIsCancellingSubscription(true);
    try {
      await paymentService.cancelSubscription(user.id);
      setShowCancelSubDialog(false);
      toast({
        title: "Subscription Cancelled",
        description:
          "Your subscription has been cancelled. You are now on the PAYG plan.",
      });
      // Refresh the page so the tier badge updates
      setTimeout(() => window.location.reload(), 1500);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Cancellation Failed",
        description:
          error.message || "Unable to cancel subscription. Please try again.",
      });
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const getTierDisplayInfo = (tier: string) => {
    switch (tier) {
      case "PAYG":
        return {
          name: "PAYG",
          color:
            "bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-600",
          icon: null,
          description: "Pay-as-you-go access with credits for premium actions",
        };
      case "professional":
        return {
          name: "Professional",
          color:
            "bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600",
          icon: <Crown className="h-4 w-4" />,
          description: "Complete solution for organizations and researchers",
        };
      default:
        return {
          name: "PAYG",
          color:
            "bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-600",
          icon: null,
          description: "Pay-as-you-go access with credits for premium actions",
        };
    }
  };

  const tierInfo = getTierDisplayInfo(tier);

  const tabs = [
    {
      id: "profile" as const,
      label: "Profile",
      icon: <User className="h-4 w-4" />,
    },
    {
      id: "security" as const,
      label: "Security",
      icon: <Shield className="h-4 w-4" />,
    },
    {
      id: "location" as const,
      label: "Location",
      icon: <MapPin className="h-4 w-4" />,
    },
    {
      id: "subscription" as const,
      label: "Subscription",
      icon: <CreditCard className="h-4 w-4" />,
    },
    {
      id: "danger" as const,
      label: "Danger Zone",
      icon: <AlertTriangle className="h-4 w-4" />,
      danger: true,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <TopBar
        title="Settings"
        icon={<User className="h-4 w-4 text-[#40e0d0]" />}
        showBackButton={true}
        showSubscriptionBadge={true}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6  py-8">
        {/* ── Profile summary header ── */}
        <div className="flex items-center gap-5 mb-8 p-5 rounded-2xl border border-border/50 bg-card dark:bg-[#fff]/5 shadow">
          {/* Avatar */}
          <div className="relative shrink-0">
            <div className="h-16 w-16 rounded-2xl overflow-hidden bg-[#40e0d0]/10 ring-2 ring-[#40e0d0]/20 flex items-center justify-center">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="h-full w-full object-cover"
                  onError={() => setAvatarUrl(null)}
                />
              ) : (
                <User className="h-8 w-8 text-[#40e0d0]/60" />
              )}
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                </div>
              )}
            </div>
            <Input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleAvatarUpload}
              disabled={isUploading || isDeleting}
              className="hidden"
              id="avatar-upload"
            />
            <button
              onClick={() => document.getElementById("avatar-upload")?.click()}
              disabled={isUploading || isDeleting}
              className="absolute -bottom-1.5 -right-1.5 h-6 w-6 rounded-full bg-[#40e0d0] text-black flex items-center justify-center shadow hover:bg-[#40e0d0]/80 transition-colors"
              title="Change photo"
            >
              <Upload className="h-3 w-3" />
            </button>
          </div>

          {/* Name + email + tier */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold truncate">
                {fullName || user?.email?.split("@")[0] || "User"}
              </h2>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${tierInfo.color}`}
              >
                {tierInfo.icon}
                {tierInfo.name}
              </span>
            </div>
            <p className="text-sm text-muted-foreground truncate text-left">
              {user?.email}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 text-left">
              Member since{" "}
              {new Date(user?.created_at || "").toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>

          {/* Sign out */}
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 gap-2 text-muted-foreground hover:text-foreground hidden sm:flex"
            onClick={() => signOut()}
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>

        {/* ── Main layout: sidebar nav + content ── */}
        <div className="flex flex-col sm:flex-row gap-6">
          {/* Sidebar navigation */}
          <nav className="sm:w-52 shrink-0 ">
            {/* Mobile: horizontal scroll tabs */}
            <div className="flex sm:flex-col gap-1 overflow-x-auto pb-1 sm:pb-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap sm:whitespace-normal sm:w-full text-left
                    ${
                      activeTab === tab.id
                        ? tab.danger
                          ? "bg-destructive/10 text-destructive"
                          : "bg-[#40e0d0]/10 text-[#40e0d0]"
                        : tab.danger
                          ? "text-destructive/70 hover:bg-destructive/5 hover:text-destructive"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                >
                  {tab.icon}
                  <span className="flex-1">{tab.label}</span>
                  {activeTab === tab.id && (
                    <ChevronRight className="h-3.5 w-3.5 hidden sm:block" />
                  )}
                </button>
              ))}
            </div>
          </nav>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            {/* ── PROFILE TAB ── */}
            {activeTab === "profile" && (
              <Card className="border-border/50 dark:bg-[#fff]/5 bg-[#40e0d0]/5 shadow">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">
                    Personal Information
                  </CardTitle>
                  <CardDescription>
                    Update your name, bio, and interests.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Avatar actions */}
                  <div className="flex items-center gap-3">
                    <div className="h-14 w-14 rounded-xl overflow-hidden bg-[#40e0d0]/10 flex items-center justify-center shrink-0">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt="Profile"
                          className="h-full w-full object-cover"
                          onError={() => setAvatarUrl(null)}
                        />
                      ) : (
                        <User className="h-7 w-7 text-[#40e0d0]/60" />
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          document.getElementById("avatar-upload")?.click()
                        }
                        disabled={isUploading || isDeleting}
                        className="gap-2"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {isUploading ? "Uploading…" : "Change photo"}
                      </Button>
                      {avatarUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteAvatar}
                          disabled={isDeleting || isUploading}
                          className="gap-2 text-destructive hover:text-destructive border-destructive/30 hover:border-destructive hover:bg-destructive/5"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {isDeleting ? "Removing…" : "Remove"}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Name + DOB */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="fullName">Full Name</Label>
                      <Input
                        id="fullName"
                        placeholder="Your full name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="dateOfBirth">Date of Birth</Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Email – read only */}
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Input
                        id="email"
                        value={user?.email || ""}
                        disabled
                        readOnly
                        className="bg-muted/40 pr-20"
                      />
                      <Badge
                        variant="outline"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs bg-green-50 text-green-700 border-green-200"
                      >
                        Verified
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Contact support to change your email.
                    </p>
                  </div>

                  {/* Bio */}
                  <div className="space-y-1.5">
                    <Label htmlFor="bio">Bio</Label>
                    <div className="relative">
                      <textarea
                        id="bio"
                        placeholder="Share a bit about yourself and your environmental interests…"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring resize-y min-h-[100px]"
                      />
                      <span className="absolute bottom-2 right-3 text-xs text-muted-foreground">
                        {bio.length}/500
                      </span>
                    </div>
                  </div>

                  {/* Environmental Interests */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Environmental Interests</Label>
                      <Badge variant="outline" className="text-xs">
                        {interests.length} selected
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "Environmental Science",
                        "Climate Change",
                        "Wildlife Conservation",
                        "Sustainable Living",
                        "Renewable Energy",
                        "Afro-Ecological Heritage",
                        "Biodiversity",
                        "Ocean Conservation",
                        "Forest Protection",
                        "Carbon Footprint",
                        "Green Technology",
                        "Eco-Tourism",
                        "Permaculture",
                        "Marine Biology",
                        "Ecology",
                        "Environmental Policy",
                      ].map((interest) => (
                        <button
                          key={interest}
                          type="button"
                          onClick={() =>
                            setInterests(
                              interests.includes(interest)
                                ? interests.filter((i) => i !== interest)
                                : [...interests, interest],
                            )
                          }
                          className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all
                            ${
                              interests.includes(interest)
                                ? "bg-[#40e0d0] text-black border-[#40e0d0]"
                                : "bg-background border-border text-muted-foreground hover:border-[#40e0d0]/50 hover:text-foreground"
                            }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="border-t border-border/40 pt-4 flex justify-end">
                  <Button
                    onClick={handleProfileUpdate}
                    disabled={isSaving}
                    className="bg-[#40e0d0] hover:bg-[#40e0d0]/90 text-black font-medium"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                        Saving…
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* ── SECURITY TAB ── */}
            {activeTab === "security" && (
              <Card className="border-border/50 dark:bg-[#fff]/5 bg-[#40e0d0]/5 shadow">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">Change Password</CardTitle>
                  <CardDescription>
                    Update your password to keep your account secure.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Current password */}
                  <div className="space-y-1.5">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        disabled={isChangingPassword}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* New password */}
                  <div className="space-y-1.5">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        disabled={isChangingPassword}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {newPassword && (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1">
                        {Object.entries({
                          minLength: "8+ characters",
                          hasUpperCase: "Uppercase (A–Z)",
                          hasLowerCase: "Lowercase (a–z)",
                          hasNumbers: "Number (0–9)",
                          hasSpecialChar: "Special character",
                        }).map(([key, label]) => {
                          const ok =
                            validatePassword(newPassword)[
                              key as keyof ReturnType<typeof validatePassword>
                            ];
                          return (
                            <span
                              key={key}
                              className={`flex items-center gap-1.5 text-xs ${ok ? "text-green-600" : "text-muted-foreground"}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-green-500" : "bg-muted-foreground/30"}`}
                              />
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Confirm password */}
                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword">
                      Confirm New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        disabled={isChangingPassword}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {confirmPassword && (
                      <p
                        className={`text-xs ${newPassword === confirmPassword ? "text-green-600" : "text-destructive"}`}
                      >
                        {newPassword === confirmPassword
                          ? "✓ Passwords match"
                          : "✗ Passwords don't match"}
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1.5 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" /> Tips
                    </p>
                    <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5">
                      <li>
                        • Use a unique password you haven't used elsewhere
                      </li>
                      <li>• Consider a password manager</li>
                      <li>• Never share your password</li>
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="border-t border-border/40 pt-4 flex justify-end">
                  <Button
                    onClick={handlePasswordChange}
                    disabled={
                      isChangingPassword ||
                      !currentPassword.trim() ||
                      !newPassword.trim() ||
                      !confirmPassword.trim() ||
                      newPassword !== confirmPassword ||
                      !validatePassword(newPassword).isValid
                    }
                    className="gap-2"
                  >
                    {isChangingPassword ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Updating…
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4" />
                        Update Password
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* ── LOCATION TAB ── */}
            {activeTab === "location" && <LocationSettings />}

            {/* ── SUBSCRIPTION TAB ── */}
            {activeTab === "subscription" && (
              <div className="space-y-4">
                {/* Current plan */}
                <Card className="border-border/50 dark:bg-[#fff]/5 bg-[#40e0d0]/5 shadow">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base">Current Plan</CardTitle>
                    <CardDescription>
                      Your active subscription and billing information.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div
                      className={`flex items-start gap-4 p-4 rounded-xl border ${tier === "professional" ? "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10" : "border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10"}`}
                    >
                      <div
                        className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${tier === "professional" ? "bg-blue-100 dark:bg-blue-900/30" : "bg-emerald-100 dark:bg-emerald-900/30"}`}
                      >
                        {tier === "professional" ? (
                          <Crown className="h-5 w-5 text-blue-600" />
                        ) : (
                          <CreditCard className="h-5 w-5 text-emerald-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">
                            {tierInfo.name} Plan
                          </span>
                          <Badge
                            variant="outline"
                            className="text-xs bg-green-50 text-green-700 border-green-200"
                          >
                            Active
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {tierInfo.description}
                        </p>
                        {tier === "professional" && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Includes 100 credits + 20 bonus credits. Billed as a
                            one-time charge via Paystack.
                          </p>
                        )}
                        {tier === "PAYG" && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Purchase credits as you need them. Upgrade to
                            Professional for a credit bundle.
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cancel subscription – only for paid tiers */}
                {tier !== "PAYG" && (
                  <Card className="border-border/50">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-base">
                        Cancel Subscription
                      </CardTitle>
                      <CardDescription>
                        Downgrade to the PAYG plan. Your existing credits are
                        kept.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
                        <div>
                          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                            Downgrade to PAYG
                          </p>
                          <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                            Professional features will be removed immediately.
                            Existing credits stay in your account.
                          </p>
                        </div>
                        <Dialog
                          open={showCancelSubDialog}
                          onOpenChange={setShowCancelSubDialog}
                        >
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30 gap-2"
                            >
                              <XCircle className="h-4 w-4" />
                              Cancel Plan
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <div className="flex items-center gap-3 mb-2">
                                <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                                  <XCircle className="h-5 w-5 text-amber-600" />
                                </div>
                                <DialogTitle>Cancel Subscription</DialogTitle>
                              </div>
                              <DialogDescription className="text-left space-y-3">
                                <p>
                                  Are you sure you want to cancel your{" "}
                                  <strong>{tierInfo.name}</strong> subscription?
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-sm">
                                  <li>
                                    Your plan is downgraded to PAYG immediately
                                  </li>
                                  <li>Unused credits remain in your account</li>
                                  <li>
                                    Professional features will no longer be
                                    available
                                  </li>
                                  <li>You can re-subscribe at any time</li>
                                </ul>
                                <p className="text-xs text-muted-foreground pt-1">
                                  Since Paystack processes one-time charges (not
                                  recurring billing), no future charges will
                                  occur — this simply removes your Professional
                                  access.
                                </p>
                              </DialogDescription>
                            </DialogHeader>
                            <div className="flex gap-3 justify-end pt-2">
                              <Button
                                variant="outline"
                                onClick={() => setShowCancelSubDialog(false)}
                                disabled={isCancellingSubscription}
                              >
                                Keep Plan
                              </Button>
                              <Button
                                className="bg-amber-600 hover:bg-amber-700 text-white min-w-[120px]"
                                onClick={handleCancelSubscription}
                                disabled={isCancellingSubscription}
                              >
                                {isCancellingSubscription ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                    Cancelling…
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Yes, Cancel
                                  </>
                                )}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* ── DANGER ZONE TAB ── */}
            {activeTab === "danger" && (
              <Card className="border-destructive/30 dark:bg-destructive/5 shadow">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                    </div>
                    <div>
                      <CardTitle className="text-base text-destructive">
                        Danger Zone
                      </CardTitle>
                      <CardDescription>
                        Irreversible actions that permanently affect your
                        account.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border border-destructive/30 bg-destructive/5">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-destructive text-sm">
                          Delete Account
                        </span>
                        <Badge
                          variant="outline"
                          className="text-xs border-destructive/30 text-destructive"
                        >
                          Permanent
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Permanently deletes your profile, data and preferences.
                        This cannot be undone.
                      </p>
                    </div>
                    <Dialog
                      open={showDeleteDialog}
                      onOpenChange={(open) => {
                        setShowDeleteDialog(open);
                        if (!open) {
                          setDeletePassword("");
                          setShowDeletePassword(false);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="shrink-0 gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Account
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
                              <AlertTriangle className="h-5 w-5 text-destructive" />
                            </div>
                            <DialogTitle>Delete Account</DialogTitle>
                          </div>
                          <DialogDescription className="text-left">
                            Are you sure you want to delete your account? This
                            action will:
                          </DialogDescription>
                        </DialogHeader>
                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                          <li>
                            Permanently delete all your environmental data
                          </li>
                          <li>Remove your profile and preferences</li>
                          <li>Cancel any active subscriptions</li>
                          <li>Cannot be undone or recovered</li>
                        </ul>
                        <div className="space-y-2 py-2">
                          {isOAuthUser ? (
                            <>
                              <Label
                                htmlFor="delete-confirm"
                                className="text-sm font-medium"
                              >
                                Type{" "}
                                <span className="font-mono font-bold">
                                  DELETE
                                </span>{" "}
                                to confirm
                              </Label>
                              <Input
                                id="delete-confirm"
                                type="text"
                                placeholder="DELETE"
                                value={deletePassword}
                                onChange={(e) =>
                                  setDeletePassword(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !isDeletingAccount)
                                    handleDeleteAccount();
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Label
                                htmlFor="delete-password"
                                className="text-sm font-medium"
                              >
                                Confirm your password
                              </Label>
                              <div className="relative">
                                <Input
                                  id="delete-password"
                                  type={
                                    showDeletePassword ? "text" : "password"
                                  }
                                  placeholder="Enter your password"
                                  value={deletePassword}
                                  onChange={(e) =>
                                    setDeletePassword(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !isDeletingAccount)
                                      handleDeleteAccount();
                                  }}
                                  className="pr-10"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowDeletePassword((v) => !v)
                                  }
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                  tabIndex={-1}
                                >
                                  {showDeletePassword ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="flex gap-3 justify-end pt-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowDeleteDialog(false);
                              setDeletePassword("");
                              setShowDeletePassword(false);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={handleDeleteAccount}
                            disabled={isDeletingAccount}
                            className="min-w-[120px]"
                          >
                            {isDeletingAccount ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                Deleting…
                              </>
                            ) : (
                              <>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Account
                              </>
                            )}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
